// Iter AY — floating "Send Feedback" button + modal, mounted in Layout.
import { useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "sonner";
import { MessageSquarePlus, X, ImagePlus, Trash2 } from "lucide-react";
import { api } from "../lib/api";

const TYPES = [
  { key: "bug", label: "Bug" },
  { key: "confusing", label: "Confusing" },
  { key: "suggestion", label: "Suggestion" },
  { key: "other", label: "Other" },
];

const compressImage = (file) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const max = 1280;
      const scale = Math.min(1, max / Math.max(img.width, img.height));
      const canvas = document.createElement("canvas");
      canvas.width = Math.round(img.width * scale);
      canvas.height = Math.round(img.height * scale);
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL("image/jpeg", 0.75));
    };
    img.onerror = reject;
    img.src = URL.createObjectURL(file);
  });

export const FeedbackButton = () => {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [type, setType] = useState("bug");
  const [comment, setComment] = useState("");
  const [screenshot, setScreenshot] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const pickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.error("Please choose an image file");
    try {
      setScreenshot(await compressImage(f));
    } catch {
      toast.error("Could not read that image");
    }
    e.target.value = "";
  };

  const submit = async () => {
    if (comment.trim().length < 3) return toast.error("Please add a short comment");
    setBusy(true);
    try {
      await api.post("/feedback", {
        type,
        comment: comment.trim(),
        page_path: location.pathname,
        user_agent: navigator.userAgent,
        screen: `${window.innerWidth}x${window.innerHeight}`,
        screenshot,
      });
      toast.success("Thanks! Your feedback was sent.");
      setOpen(false);
      setComment("");
      setScreenshot(null);
      setType("bug");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not send feedback");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="feedback-fab"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-[60] min-h-[44px] px-4 rounded-full bg-ink-900 hover:bg-ink-900/90 text-white text-sm font-medium shadow-lg inline-flex items-center gap-2 transition-transform hover:scale-[1.03] print:hidden"
      >
        <MessageSquarePlus className="w-4 h-4" strokeWidth={1.5} />
        <span className="hidden sm:inline">Send Feedback</span>
        <span className="sm:hidden">Feedback</span>
      </button>

      {open && (
        <div className="fixed inset-0 z-[70] bg-ink-900/50 backdrop-blur-sm flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="feedback-modal">
          <div className="bg-white w-full sm:max-w-md rounded-t-3xl sm:rounded-3xl border border-paper-200 p-6 sm:p-7 max-h-[92vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="font-heading text-xl text-ink-900">Send feedback</div>
                <p className="text-xs text-ink-500 mt-0.5">Goes straight to the Posturra team — please don&rsquo;t include client details.</p>
              </div>
              <button data-testid="feedback-close" onClick={() => setOpen(false)} className="w-9 h-9 rounded-full hover:bg-paper-100 flex items-center justify-center text-ink-500 shrink-0">
                <X className="w-4 h-4" strokeWidth={1.5} />
              </button>
            </div>

            <div className="flex flex-wrap gap-2 mb-4">
              {TYPES.map((t) => (
                <button
                  key={t.key}
                  data-testid={`feedback-type-${t.key}`}
                  onClick={() => setType(t.key)}
                  className={`min-h-[38px] px-4 rounded-full text-sm border transition-colors ${type === t.key ? "bg-ink-900 text-white border-ink-900" : "bg-white border-paper-200 text-ink-700 hover:bg-paper-100"}`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            <textarea
              data-testid="feedback-comment"
              rows={4}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="What happened? What did you expect?"
              className="w-full px-4 py-3 rounded-2xl bg-white border border-paper-200 focus:ring-2 focus:ring-sage-500/30 focus:border-sage-500 outline-none text-ink-900 text-sm resize-none mb-3"
            />

            {screenshot ? (
              <div className="relative mb-4">
                <img src={screenshot} alt="Screenshot preview" className="rounded-2xl border border-paper-200 max-h-40 w-auto" data-testid="feedback-screenshot-preview" />
                <button data-testid="feedback-screenshot-remove" onClick={() => setScreenshot(null)} className="absolute top-2 right-2 w-8 h-8 rounded-full bg-ink-900/80 text-white flex items-center justify-center">
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
              </div>
            ) : (
              <button data-testid="feedback-screenshot-add" onClick={() => fileRef.current?.click()} className="mb-4 min-h-[40px] px-4 rounded-full border border-dashed border-paper-200 hover:bg-paper-100 text-ink-500 text-xs inline-flex items-center gap-2">
                <ImagePlus className="w-4 h-4" strokeWidth={1.5} /> Attach a screenshot (optional)
              </button>
            )}
            <input ref={fileRef} type="file" accept="image/*" onChange={pickFile} className="hidden" data-testid="feedback-file-input" />

            <button
              data-testid="feedback-submit"
              onClick={submit}
              disabled={busy}
              className="w-full min-h-[48px] rounded-full bg-sage-500 hover:bg-sage-600 disabled:opacity-50 text-white font-medium text-sm transition-all"
            >
              {busy ? "Sending…" : "Send feedback"}
            </button>
            <p className="text-[10px] text-ink-500 mt-3 text-center">Your name, email, current page, and browser info are included automatically.</p>
          </div>
        </div>
      )}
    </>
  );
};
